import * as React from 'react'
import { bind } from 'classnames/bind'
import * as styles from '@procore/core-css/src/layouts/_sidebar.scss'

import addSubcomponents from '../utils/addSubcomponents'
import {
  Props,
  DivAttributes,
  AnchorAttributes,
  OlAttributes,
  LiAttributes,
} from '../utils/types'

const cx = bind(styles)

interface SidebarMenuItemProps extends Props {
  active?: boolean
}

export const Sidebar = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div ref={ref} className={cx('core-l-sidebar', className)} {...props}>
      {children}
    </div>
  )
)

export const Divider = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cx('core-l-sidebar__divider', className)}
      {...props}
    />
  )
)

export const Button = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cx('core-l-sidebar__button', className)}
      {...props}
    >
      {children}
    </div>
  )
)

export const Header = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cx('core-l-sidebar__header', className)}
      {...props}
    >
      {children}
    </div>
  )
)

export const HeaderLabel = React.forwardRef<
  HTMLDivElement,
  DivAttributes & Props
>(({ children, className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cx('core-l-sidebar__header__label', className)}
    {...props}
  >
    {children}
  </h2>
))

export const HeaderAction = React.forwardRef<
  HTMLAnchorElement,
  AnchorAttributes & Props
>(({ children, className, ...props }, ref) => (
  <a
    ref={ref}
    className={cx('core-l-sidebar__header__action', className)}
    {...props}
  >
    {children}
  </a>
))

export const Menu = React.forwardRef<HTMLOListElement, OlAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <ol ref={ref} className={cx('core-l-sidebar__menu', className)} {...props}>
      {children}
    </ol>
  )
)

export const MenuItem = React.forwardRef<
  HTMLLIElement,
  LiAttributes & SidebarMenuItemProps
>(({ active = false, children, className, ...props }, ref) => (
  <li
    ref={ref}
    className={cx(
      'core-l-sidebar__menu__item',
      {
        ['core-l-sidebar__menu__item--active']: active,
      },
      className
    )}
    {...props}
  >
    {children}
  </li>
))

Sidebar.displayName = 'Sidebar'

Divider.displayName = 'Sidebar.Divider'

Button.displayName = 'Sidebar.Button'

Header.displayName = 'Sidebar.Header'

HeaderLabel.displayName = 'Sidebar.HeaderLabel'

HeaderAction.displayName = 'Sidebar.HeaderAction'

Menu.displayName = 'Sidebar.'

MenuItem.displayName = 'Sidebar.'

export default addSubcomponents(
  {
    Divider,
    Button,
    Header,
    HeaderLabel,
    HeaderAction,
    Menu,
    MenuItem,
  },
  Sidebar
)
